import { memo } from 'react';

import styled from 'styled-components';
import Radio from 'antd/es/radio';
import { usePathname } from 'next/navigation';

import { FlexibleDiv, Row } from '@components/formStyles';
import Collapse from '@components/Collapse/Collapse';
import { SelectionFilterHeading } from '@components/views/hotel/listing/components/Filters/SelectionFilterStyle';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';

import { EntityFilter } from '../FiltersBlock';

export const Text = styled.span`
  color: ${({ theme }) => theme.palette.grey1100};
  font-size: 14px;
  font-weight: 400;
`;
export const ScoreDots = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const ScoreDot = styled.span<{ $filled: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: solid 1.5px ${({ theme }) => theme.palette.green600};
  background-color: ${({ $filled, theme }) =>
    $filled ? theme.palette.green600 : 'transparent'};
`;

export const ScoreLabel = styled(Text)`
  font-weight: 600;
`;

export const StyledRadio = styled(Radio)`
  &&& {
    color: #fff;

    .setur-radio-checked .setur-radio-inner {
      background-color: ${({ theme }) => theme.palette.white};

      &:after {
        transform: scale(1);
        border: 4.5px solid ${({ theme }) => theme.palette.primaryBrand};
      }
    }
  }
`;
export interface NameFilterProps {
  title?: string;
  point?: EntityFilter[];
  handleSelectedItems?: (item: string) => void;
  selectedItems?: string | string[];
  collapsible?: boolean;
  variant?: 'dots' | 'text';
}

export const REVIEW_FILTER_ALL = 'all';

const MAX_SCORE = 5;

const SCORE_LABELS: Record<number, string> = {
  5: 'Mükemmel',
  4: 'Harika',
  3: 'Çok İyi',
  2: 'İyi',
};

const ReviewFilter = ({
  title,
  point,
  handleSelectedItems: onSelectItems,
  selectedItems,
  collapsible = false,
  variant = 'dots',
}: NameFilterProps) => {
  const { push } = useAppRouter();
  const query = useSearchParamsToQuery();
  const pathname = usePathname();

  const handleSelectAnyone = () => {
    const updatedQuery = query;
    delete updatedQuery['point'];

    handleSelectItems(REVIEW_FILTER_ALL);
    push({ pathname: pathname ?? '', query: updatedQuery });
  };

  const handleSelectItems = (id: string) => {
    onSelectItems?.(id);
  };

  const scoreItems = point?.[0]?.items?.slice(0)?.reverse() ?? [];

  const content = (
    <>
      {scoreItems.map((i) => {
        const score = Number(i.key) || 0;

        return (
          <Row alignItems='center' key={i.key} style={{ marginBottom: 16 }}>
            <StyledRadio
              onClick={() => {
                if (selectedItems == i.key) handleSelectAnyone();
              }}
              onChange={() => handleSelectItems(i.key)}
              type='radio'
              name='discount'
              checked={selectedItems == i.key}
            />
            {variant === 'dots' ? (
              <FlexibleDiv left={4}>
                <ScoreDots>
                  {Array.from({ length: MAX_SCORE }, (_, index) => (
                    <ScoreDot key={index} $filled={index < score} />
                  ))}
                </ScoreDots>
              </FlexibleDiv>
            ) : null}
            <FlexibleDiv left={8}>
              <ScoreLabel>
                {variant === 'dots'
                  ? score === MAX_SCORE
                    ? i.key
                    : `${i.key} ve üzeri`
                  : `${SCORE_LABELS[score] ?? ''} ${i.key}${
                      score === MAX_SCORE ? '' : '+'
                    }`.trim()}
              </ScoreLabel>
            </FlexibleDiv>
          </Row>
        );
      })}
    </>
  );

  return collapsible ? (
    <Collapse
      defaultActiveKey='review-filter'
      panelProps={{
        header: <SelectionFilterHeading>{title}</SelectionFilterHeading>,
        key: 'review-filter',
      }}
    >
      {content}
    </Collapse>
  ) : (
    <>
      <FlexibleDiv bottom={16}>
        <SelectionFilterHeading>{title}</SelectionFilterHeading>
      </FlexibleDiv>
      {content}
    </>
  );
};

export default memo(ReviewFilter);
